import {computed, effect, signal,} from "@preact/signals-react";
import {loginWithOtpApi, loginWithGoogle, setAccessToken, validateOTP, validateUserOTP} from "@/services/auth-service";
import {ISession, toSession} from "@/app/dashboard/models/Auth";
import {getProfile, updateProfile, updateUser} from "@/services/user-service";
import {userDetails} from "@/store/user.store";
import {showCommonApiError, showError, showSuccess} from "@/lib/toast-utill";
import {getStoreItem, LocalStorageKeys, removeStoreItem} from "@/lib/localStorage-utill";
import { isValidPhoneNumber } from "react-phone-number-input";

export const email = signal<string>();
export const phone = signal<string>();
export const otpExpire = signal<string>();
export const userInputOtp = signal<string>();
export const session = signal<ISession | null>(null);

export const firstNameUserInput = signal<string>();
export const lastNameUserInput = signal<string>();

export enum LoginFormStep {
    Initial,
    ValidateOTP,
    FirstLoginForm,
    Success
}

export enum LoginMethod {
    Email = 'email',
    Phone = 'phone'
}

export const loginFormStep = signal<LoginFormStep>(LoginFormStep.Initial);
export const loginMethod = signal<LoginMethod>(LoginMethod.Email);

export const isLoggedIn = computed(() => !!session.value?.accessToken && !session.value.isNewUser)

export const isOpenLoginModal = signal<boolean>(false)

export const isOpenHeaderMenu = signal<boolean>(false);
export const isSendingOtp = signal(false);
export const isPhoneValidationModalOpen = signal(false);
export const isAdditionalContactValidation = signal(false);
export const additionalContactType = signal<'email' | 'phone'>('email');
export const additionalContactValue = signal('');
export const isAdditionalContactValid = signal(false);
export const additionalContactError = signal('');
export const isAdditionalOtpSent = signal(false);
export const additionalOtpValue = signal('');
export const isAdditionalOtpValid = signal(true);
export const additionalOtpError = signal('');
export const isSendingAdditionalOtp = signal(false);
export const isVerifyingAdditionalOtp = signal(false);
export const isAdditionalContactVerified = signal(false);
export const originalAdditionalContactValue = signal('');

export const hasCheckedAuth = signal<boolean | null>(null);

export const setIsOpenHeaderMenu = (isOpen = false) => isOpenHeaderMenu.value = isOpen;


export const setEmailText = (text: string) => {
    email.value = text;
}

export const setPhoneText = (text: string) => {
    phone.value = text;
}

export const openLoginModal = () => {
    setIsOpenModal(true)
}
export const closeLoginModal = () => {
    setIsOpenModal(false)
}

export const setIsOpenModal = (isOpen: boolean) => {
    isOpenLoginModal.value = isOpen;
}

const navigateToStep = (step: LoginFormStep) => {
    loginFormStep.value = step;
}

export const loginWithEmail = async () => {
    if (!email.value) {
        showError("Please enter a valid email");
        return;
    }
    try {
        isSendingOtp.value = true;
        const [, error] = await loginWithOtpApi(email.value, null);
        if (error) {
            showCommonApiError("login with email");
            return;
        }
        navigateToStep(LoginFormStep.ValidateOTP);
    } finally {
        isSendingOtp.value = false;
    }
}

export const loginWithPhone = async () => {
    if (!phone.value) {
        showError("Please enter a valid phone number");
        return;
    }
    try {
        isSendingOtp.value = true;
        const [, error] = await loginWithOtpApi(null, phone.value);
        if (error) {
            showCommonApiError("login with phone");
            return;
        }
        navigateToStep(LoginFormStep.ValidateOTP);
    } finally {
        isSendingOtp.value = false;
    }
}

export const loginGmailEmail = async (idToken: string) => {
    const [gmailLogin, error] = await loginWithGoogle(idToken);
    if (error) {
        showCommonApiError("login with email")
        return;
    }

    session.value = toSession(gmailLogin);
    setAccessToken(session.value.accessToken);
    await loadUserDetails(true);
    closeLoginModal()
}

export const validatePhoneNumber = (phoneNumber: string): boolean => {
    return !!phoneNumber;
}

export const handlePhoneValidation = (phoneNumber: string) => {
    setPhoneText(phoneNumber || '');
    return validatePhoneNumber(phoneNumber || '');
}

export const resendOtp = async () => {
    const isPhoneVerification = loginMethod.value === LoginMethod.Phone;
    const [, error] = await loginWithOtpApi(
        isPhoneVerification ? null : email.value,
        isPhoneVerification ? phone.value : null
    );
    if (error) {
        showCommonApiError("sending OTP")
        return;
    }
    showSuccess("Re-send the OTP successfully")
}


export const setUserInputOTP = (text: string) => {
    userInputOtp.value = text;
}

export const validateOtp = async () => {
    const isPhoneVerification = loginMethod.value === LoginMethod.Phone;
    const [response, error] = await validateOTP(
        userInputOtp.value,
        isPhoneVerification ? null : email.value,
        isPhoneVerification ? phone.value : null
    );

    if (error) {
        showError("Please enter a valid OTP code", "Invalid OTP");
        return;
    }

    session.value = toSession(response);
    setAccessToken(session.value.accessToken);
    
    if (response.isNewUser) {
        // For new users, determine what additional contact info they need
        try {
            await loadUserDetails(false); // Load profile without triggering phone validation modal
        } catch (error) {
            console.error("Failed to load user details for new user:", error);
            // Continue with the flow even if profile loading fails
        }
        
        const profile = userDetails.value;
        if (profile) {
            // If user logged in with email but doesn't have phone, ask for phone
            if (isPhoneVerification && !profile.email) {
                setAdditionalContactType('email');
                isAdditionalContactValidation.value = true;
            } else if (!isPhoneVerification && !profile.contactNumber) {
                setAdditionalContactType('phone');
                isAdditionalContactValidation.value = true;
            }
        } else {
            // If profile loading failed, still set up additional contact validation based on login method
            if (isPhoneVerification) {
                setAdditionalContactType('email');
                isAdditionalContactValidation.value = true;
            } else {
                setAdditionalContactType('phone');
                isAdditionalContactValidation.value = true;
            }
        }
        
        navigateToStep(LoginFormStep.FirstLoginForm);
        showSuccess('Please enter your first name and last name', "Welcome to Toutix!");
        return;
    }

    await loadUserDetails(true);
    closeLoginModal();
}


export const loadUserDetails = async (isAfterLogin: boolean = false) => {
    const [profile, profileError] = await getProfile()
    if (profileError) {
        return logout();
    }

    userDetails.value = profile
    
    // Check if phone validation is needed only after login, not on page reload
    if (isAfterLogin && profile && !profile.isPhoneValidated) {
        isPhoneValidationModalOpen.value = true;
    }
}


export const setFirstName = (text: string) => {
    firstNameUserInput.value = text
}

export const setLastName = (text: string) => {
    lastNameUserInput.value = text
}

export const submitFirstNameAndLastName = async () => {
    // Prepare update data with all required fields
    const updateData: any = {
        firstName: firstNameUserInput.value,
        lastName: lastNameUserInput.value,
        address: userDetails.value?.address,
        dateOfBirth: userDetails.value?.dateOfBirth ? new Date(userDetails.value.dateOfBirth).toISOString() : undefined,
    };

    // Include original contact information from the initial login
    if (loginMethod.value === LoginMethod.Phone) {
        // User logged in with phone, so include the phone number
        updateData.contactNumber = phone.value;
        updateData.isPhoneValidated = true;
    } else {
        // User logged in with email, so include the email
        updateData.email = email.value;
    }

    // Add additional contact information if it was validated
    if (isAdditionalContactValidation.value && isAdditionalContactVerified.value) {
        if (additionalContactType.value === 'email') {
            updateData.email = additionalContactValue.value;
        } else {
            updateData.contactNumber = additionalContactValue.value;
            updateData.isPhoneValidated = true;
        }
    }

    const [profile, error] = await updateUser(updateData);

    if (error) {
        showCommonApiError("updating profile")
        return;
    }

    userDetails.value = profile;
    session.value = {...session.value, isNewUser: false};
    
    // Check if phone validation is needed after first login
    if (profile && !profile.isPhoneValidated) {
        isPhoneValidationModalOpen.value = true;
    }
    
    closeLoginModal()
}

export const logout = () => {
    userDetails.value = null
    session.value = null;
    removeStoreItem(LocalStorageKeys.AccessToken)
    hasCheckedAuth.value = false;
    loginFormStep.value = LoginFormStep.Initial;
}

const clearFormDetails = () => {
    email.value = "";
    phone.value = "";
    otpExpire.value = "";
    firstNameUserInput.value = "";
    lastNameUserInput.value = "";
    userInputOtp.value = "";
}

export const loadSessionByToken = async () => {
    const accessToken = getStoreItem<string>(LocalStorageKeys.AccessToken)
    
    if (!accessToken) {
        hasCheckedAuth.value = false;
        return
    }
    session.value = {accessToken, isNewUser: false}
    await loadUserDetails(false);
    hasCheckedAuth.value = true;
}

effect(() => {
    if (session.value?.accessToken) {
        setAccessToken(session.value.accessToken)
    }
})

export const clearLoginFormModalDetails = () => {
    clearFormDetails()
    navigateToStep(LoginFormStep.Initial);
}

effect(() => {
    if (!isOpenLoginModal.value) {
        clearLoginFormModalDetails()
    }
})

export const closePhoneValidationModal = () => {
    isPhoneValidationModalOpen.value = false;
}

export const openPhoneValidationModal = () => {
    isPhoneValidationModalOpen.value = true;
}

export const setAdditionalContactType = (type: 'email' | 'phone') => {
    additionalContactType.value = type;
    additionalContactValue.value = '';
    isAdditionalContactValid.value = false; // Changed from true to false
    additionalContactError.value = '';
    isAdditionalOtpSent.value = false;
    additionalOtpValue.value = '';
}

export const setAdditionalContactValue = (value: string) => {
    additionalContactValue.value = value;
    
    // If the value has changed from the originally verified value, reset verification status
    if (isAdditionalContactVerified.value && value !== originalAdditionalContactValue.value) {
        isAdditionalContactVerified.value = false;
        isAdditionalOtpSent.value = false;
        additionalOtpValue.value = '';
    }
    
    if (additionalContactType.value === 'email') {
        if (!value) {
            isAdditionalContactValid.value = false;
            additionalContactError.value = '';
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            const isValid = emailRegex.test(value);
            isAdditionalContactValid.value = isValid;
            additionalContactError.value = isValid ? '' : 'Please enter a valid email address';
        }
    } else {
        // Use the same validation logic as the phone validation modal
        if (!value) {
            isAdditionalContactValid.value = false;
            additionalContactError.value = '';
        } else if (!value.startsWith('+')) {
            isAdditionalContactValid.value = false;
            additionalContactError.value = "Please include country code (e.g., +1 for US, +44 for UK)";
        } else if (!isValidPhoneNumber(value)) {
            if (value.length < 8) {
                isAdditionalContactValid.value = false;
                additionalContactError.value = "Phone number is too short. Please enter a complete number.";
            } else if (value.length > 15) {
                isAdditionalContactValid.value = false;
                additionalContactError.value = "Phone number is too long. Please check your number.";
            } else if (!/^\+[1-9]\d{1,14}$/.test(value)) {
                isAdditionalContactValid.value = false;
                additionalContactError.value = "Invalid phone number format. Please check your number.";
            } else {
                isAdditionalContactValid.value = false;
                additionalContactError.value = "Please enter a valid phone number for your country.";
            }
        } else {
            isAdditionalContactValid.value = true;
            additionalContactError.value = '';
        }
    }
}

export const sendAdditionalContactOtp = async () => {
    if (!isAdditionalContactValid.value) return;
    
    isSendingAdditionalOtp.value = true;
    try {
        if (additionalContactType.value === 'email') {
            const [, error] = await loginWithOtpApi(additionalContactValue.value, null);
            if (error) {
                showCommonApiError("sending OTP to email");
                return;
            }
        } else {
            const [, error] = await loginWithOtpApi(null, additionalContactValue.value);
            if (error) {
                showCommonApiError("sending OTP to phone");
                return;
            }
        }
        
        isAdditionalOtpSent.value = true;
        showSuccess(`OTP sent to your ${additionalContactType.value}`);
    } finally {
        isSendingAdditionalOtp.value = false;
    }
}

export const setAdditionalOtpValue = (value: string) => {
    additionalOtpValue.value = value;
    // Clear any existing OTP errors when user starts typing
    isAdditionalOtpValid.value = true;
    additionalOtpError.value = '';
}

export const verifyAdditionalContactOtp = async () => {
    if (!additionalOtpValue.value) {
        isAdditionalOtpValid.value = false;
        additionalOtpError.value = 'Please enter the OTP code';
        return;
    }
    
    isVerifyingAdditionalOtp.value = true;
    try {
        const [response, error] = await validateUserOTP(
            additionalOtpValue.value,
            additionalContactType.value === 'email' ? additionalContactValue.value : null,
            additionalContactType.value === 'phone' ? additionalContactValue.value : null
        );
        
        if (error) {
            isAdditionalOtpValid.value = false;
            additionalOtpError.value = 'Invalid OTP code. Please try again.';
            return;
        }
        
        // Clear any existing OTP errors since verification was successful
        isAdditionalOtpValid.value = true;
        additionalOtpError.value = '';
        
        // Get current user details, reload if needed
        let currentUser = userDetails.value;
        if (!currentUser) {
            // Try to reload user details if they're not available
            await loadUserDetails(false);
            currentUser = userDetails.value;
        }
        
        if (!currentUser) {
            showError("User details not found. Please try again.");
            return;
        }
        
        const updateData: any = {
            firstName: currentUser.firstName,
            lastName: currentUser.lastName,
            contactNumber: currentUser.contactNumber,
            address: currentUser.address,
            dateOfBirth: currentUser.dateOfBirth ? new Date(currentUser.dateOfBirth).toISOString() : undefined,
        };
        
        if (additionalContactType.value === 'email') {
            updateData.email = additionalContactValue.value;
        } else {
            updateData.contactNumber = additionalContactValue.value;
            updateData.isPhoneValidated = true;
        }
        
        const [updatedProfile, updateError] = await updateUser(updateData);
        
        if (updateError) {
            showError("Failed to update contact information. Please try again.");
            return;
        }
        
        userDetails.value = updatedProfile;
        showSuccess(`${additionalContactType.value === 'email' ? 'Email' : 'Phone number'} verified successfully!`);
        
        // Set verification status and store original value
        isAdditionalContactVerified.value = true;
        originalAdditionalContactValue.value = additionalContactValue.value;
        isAdditionalOtpSent.value = false;
        additionalOtpValue.value = '';
        
    } finally {
        isVerifyingAdditionalOtp.value = false;
    }
}

export const resendAdditionalOtp = async () => {
    await sendAdditionalContactOtp();
}